/**
 * Currency formatting utilities for OpsFlow
 * Handles proper abbreviation: k (thousands), M (millions), B (billions)
 */

/**
 * Format a number as GBP currency with proper abbreviation
 * @param {number} value - The value to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 * 
 * Examples:
 *   999 → £999
 *   1000 → £1k
 *   1500 → £1.5k
 *   10000 → £10k
 *   250000 → £250k
 *   1000000 → £1M
 *   10000000 → £10M
 *   1500000000 → £1.5B
 */
export const formatCurrencyCompact = (value, options = {}) => {
  const {
    currency = '£',
    maxDecimals = 1,
    removeTrailingZero = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return `${currency}0`;
  }

  const absValue = Math.abs(value);
  let formattedValue;
  let suffix = '';

  if (absValue >= 1_000_000_000) {
    // Billions
    formattedValue = absValue / 1_000_000_000;
    suffix = 'B';
  } else if (absValue >= 1_000_000) {
    // Millions
    formattedValue = absValue / 1_000_000;
    suffix = 'M';
  } else if (absValue >= 1_000) {
    // Thousands
    formattedValue = absValue / 1_000;
    suffix = 'k';
  } else {
    // Under 1000 - no suffix
    formattedValue = absValue;
    suffix = '';
  }

  // Format with decimals
  let result = formattedValue.toFixed(maxDecimals);
  
  // Remove trailing .0 if option enabled
  if (removeTrailingZero) {
    result = result.replace(/\.0+$/, '');
  }

  // Handle negative values
  const sign = value < 0 ? '-' : '';
  
  return `${sign}${currency}${result}${suffix}`;
};

/**
 * Format a number as full GBP currency with thousands separators
 * @param {number} value - The value to format
 * @returns {string} Formatted currency string (e.g., £1,234,567.89)
 */
export const formatCurrencyFull = (value, options = {}) => {
  const {
    currency = '£',
    decimals = 2,
    showDecimals = true
  } = options;

  if (value === null || value === undefined || isNaN(value)) {
    return `${currency}0`;
  }

  const formatted = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: showDecimals ? decimals : 0,
    maximumFractionDigits: showDecimals ? decimals : 0
  }).format(value);

  return `${currency}${formatted}`;
};

/**
 * Format a percentage value
 * @param {number} value - The value to format (0-100 or 0-1)
 * @param {boolean} isDecimal - If true, value is 0-1, multiply by 100
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, isDecimal = false) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  const percent = isDecimal ? value * 100 : value;
  return `${percent.toFixed(1).replace(/\.0$/, '')}%`;
};

export default {
  formatCurrencyCompact,
  formatCurrencyFull,
  formatPercentage
};
